// Phase 7 R5 — Read-only procurement timeline card for the PO detail page.
//
// DISPLAY ORDER: Newest first (matches the hook's `created_at DESC` query).
// This is intentional — operators triaging a PO almost always want the most
// recent activity at the top. The card never mutates and never infers state.
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  FilePlus2,
  ListPlus,
  Send,
  PackageCheck,
  DollarSign,
  Undo2,
  CircleDot,
} from 'lucide-react';
import { useState } from 'react';
import {
  useProcurementTimeline,
  type ProcurementEvent,
  type ProcurementEventType,
} from '@/hooks/purchasing/useProcurementTimeline';
import type { PurchaseOrderLine } from '@/hooks/purchasing/usePurchaseOrder';

const fmtDateTime = (s: string) => new Date(s).toLocaleString();

const fmtMoney = (n: unknown): string => {
  const num = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const ICON_BY_TYPE: Record<string, JSX.Element> = {
  po_created: <FilePlus2 className="h-3.5 w-3.5" />,
  po_line_added: <ListPlus className="h-3.5 w-3.5" />,
  po_submitted: <Send className="h-3.5 w-3.5" />,
  qty_received: <PackageCheck className="h-3.5 w-3.5" />,
  cost_corrected: <DollarSign className="h-3.5 w-3.5" />,
  qty_returned: <Undo2 className="h-3.5 w-3.5" />,
};

function lineDescription(
  lineId: string | null | undefined,
  payload: Record<string, unknown> | null,
  lines: PurchaseOrderLine[],
): string {
  if (lineId) {
    const match = lines.find((l) => l.id === lineId);
    if (match?.description) return match.description;
  }
  const desc = payload?.['description'];
  if (typeof desc === 'string' && desc.trim()) return desc;
  return 'line';
}

function summarize(
  ev: ProcurementEvent,
  lines: PurchaseOrderLine[],
): string {
  const p = (ev.payload ?? {}) as Record<string, unknown>;
  const desc = lineDescription(ev.purchase_order_line_id, p, lines);

  switch (ev.event_type as ProcurementEventType) {
    case 'po_created':
      return 'PO created';
    case 'po_line_added': {
      const qty = num(p.quantity) ?? num(p.quantity_ordered);
      return qty != null ? `Line added: ${desc} × ${qty}` : `Line added: ${desc}`;
    }
    case 'po_submitted': {
      const mirrored = num(p.mirrored_count) ?? num(p.mirrored);
      const linked = num(p.linked_count) ?? num(p.linked);
      if (mirrored != null && linked != null) {
        return `PO submitted (${mirrored} of ${linked} linked lines mirrored)`;
      }
      return 'PO submitted';
    }
    case 'qty_received': {
      const qty = num(p.quantity_received) ?? num(p.quantity);
      const cum = num(p.cumulative_received);
      const ordered = num(p.quantity_ordered);
      const tail =
        cum != null && ordered != null ? ` (cumulative ${cum}/${ordered})` : '';
      return qty != null ? `Received ${qty} of ${desc}${tail}` : `Received ${desc}${tail}`;
    }
    case 'cost_corrected': {
      const ordered = num(p.unit_cost_ordered);
      const actual = num(p.unit_cost_actual);
      const delta = num(p.delta);
      if (ordered != null && actual != null) {
        const dStr = delta != null ? ` (Δ ${fmtMoney(delta)})` : '';
        return `Cost variance on ${desc}: ${fmtMoney(ordered)} → ${fmtMoney(actual)}${dStr}`;
      }
      return `Cost variance recorded on ${desc}`;
    }
    case 'qty_returned': {
      const qty = num(p.quantity_returned) ?? num(p.quantity);
      const netKept = num(p.net_kept);
      const tail = netKept != null ? ` (net kept ${netKept})` : '';
      return qty != null ? `Returned ${qty} of ${desc}${tail}` : `Returned ${desc}${tail}`;
    }
    default:
      return ev.event_type;
  }
}

interface Props {
  poId: string | undefined;
  lines: PurchaseOrderLine[];
}

export function ProcurementTimelineCard({ poId, lines }: Props) {
  const { data, isLoading } = useProcurementTimeline(poId);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Procurement Timeline {data && `(${data.length})`}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Read-only audit history. Newest activity first.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !data || data.length === 0 ? (
          <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            No procurement events yet.
          </div>
        ) : (
          <ol className="relative space-y-2">
            {data.map((ev) => {
              const isOpen = expanded === ev.id;
              const icon = ICON_BY_TYPE[ev.event_type] ?? <CircleDot className="h-3.5 w-3.5" />;
              const summary = summarize(ev, lines);
              const hasPayload = ev.payload && Object.keys(ev.payload).length > 0;
              return (
                <li key={ev.id}>
                  <Collapsible
                    open={isOpen}
                    onOpenChange={(v) => setExpanded(v ? ev.id : null)}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="w-full flex items-start gap-3 rounded-md border bg-card px-3 py-2 text-left text-sm hover:bg-accent/30 transition-colors"
                      >
                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          {icon}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium leading-tight">{summary}</div>
                          <div className="text-xs text-muted-foreground">
                            {fmtDateTime(ev.created_at)} · {ev.actor_name ?? 'System'}
                          </div>
                        </div>
                        {hasPayload && (
                          <ChevronDown
                            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        )}
                      </button>
                    </CollapsibleTrigger>
                    {hasPayload && (
                      <CollapsibleContent>
                        <pre className="mt-2 ml-9 overflow-x-auto rounded-md border bg-muted/10 p-2 text-[11px] text-muted-foreground">
                          {JSON.stringify(ev.payload, null, 2)}
                        </pre>
                      </CollapsibleContent>
                    )}
                  </Collapsible>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
